import { useMemo } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  useTheme,
  Divider,
} from '@mui/material';
import ApiDebugComponent from '../components/ApiDebugComponent';
import { servicePredictorApiService } from '../services/servicePredictorApiService';
import { useAppSelector } from '../store/hooks';

const About = () => {
  const theme = useTheme();
  const { operatorKey } = useAppSelector((state) => state.auth);

  // Determine if debug mode should be enabled
  const isDebugMode = useMemo(() => {
    const config = window.runtimeConfig || {};
    const devOnlyFeature = config.TSP_DEV_ONLY_FEATURE === 'true';
    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '';
    return devOnlyFeature || isLocalhost;
  }, []);

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 80px)',
        py: 4,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant="h1"
          sx={{
            textAlign: 'center',
            mb: 4,
            color: theme.palette.text.primary,
          }}
        >
          About Service Availability Predictor
        </Typography>

        <Paper
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: theme.palette.background.paper,
            borderLeft: `4px solid ${theme.palette.primary.main}`,
          }}
        >
          <Typography
            variant="h2"
            sx={{ mb: 2, color: theme.palette.text.primary }}
          >
            Overview
          </Typography>
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
            }}
          >
            The Service Availability Predictor is a comprehensive tool designed
            to help network operators and service providers predict and analyze
            service availability across different regions and network
            infrastructures.
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: theme.palette.background.paper,
            borderLeft: `4px solid ${theme.palette.primary.main}`,
          }}
        >
          <Typography
            variant="h2"
            sx={{ mb: 2, color: theme.palette.text.primary }}
          >
            Capabilities
          </Typography>
          <Box
            component="ul"
            sx={{ pl: 3, m: 0, color: theme.palette.text.secondary }}
          >
            {[
              'Base Node Discovery and Analysis',
              'Operator Network Evaluation',
              'Country-based Service Predictions',
              'Sector Loss Calculations',
              'API Key Health Monitoring',
            ].map((capability, index) => (
              <Box component="li" key={index} sx={{ mb: 1, lineHeight: 1.8 }}>
                <Typography sx={{ color: theme.palette.text.secondary }}>
                  {capability}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        <Paper
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: theme.palette.background.paper,
            borderLeft: `4px solid ${theme.palette.primary.main}`,
          }}
        >
          <Typography
            variant="h2"
            sx={{ mb: 2, color: theme.palette.text.primary }}
          >
            Technology Stack
          </Typography>
          <Box
            component="ul"
            sx={{ pl: 3, m: 0, color: theme.palette.text.secondary }}
          >
            {[
              'React 19 with TypeScript',
              'Redux Toolkit for state management',
              'React Router for navigation',
              'Axios for API communication',
              'Material-UI (MUI) for styling',
            ].map((tech, index) => (
              <Box component="li" key={index} sx={{ mb: 1, lineHeight: 1.8 }}>
                <Typography sx={{ color: theme.palette.text.secondary }}>
                  {tech}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {isDebugMode && (
          <>
            <Divider sx={{ my: 4 }} />
            <Paper
              sx={{
                p: 3,
                backgroundColor: theme.palette.background.paper,
                borderLeft: `4px solid ${theme.palette.warning.main}`,
              }}
            >
              <Typography
                variant="h2"
                sx={{ mb: 2, color: theme.palette.text.primary }}
              >
                Development Section
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 3,
                  color: theme.palette.text.secondary,
                }}
              >
                Test API calls (excluding authentication). Results will be
                displayed in text boxes that wrap and grow.
              </Typography>

              <ApiDebugComponent
                title="Load Country List"
                buttonText="Load Country List"
                apiCall={async () => {
                  return await servicePredictorApiService.loadCountryList({});
                }}
              />

              <ApiDebugComponent
                title="Find Base Nodes"
                buttonText="Find Base Nodes (Test Parameters)"
                apiCall={async () => {
                  if (!operatorKey) {
                    throw new Error(
                      'Operator key is required. Please authenticate first.'
                    );
                  }
                  return await servicePredictorApiService.findBNs({
                    operator: 'Sail Internet',
                    latitude: 37.421157,
                    longitude: -121.887833,
                    radius: 13160,
                    operatorKey,
                  });
                }}
              />

              <ApiDebugComponent
                title="Calculate Lost Sectors"
                buttonText="Calculate Lost Sectors (Test Parameters)"
                apiCall={async () => {
                  if (!operatorKey) {
                    throw new Error(
                      'Operator key is required. Please authenticate first.'
                    );
                  }
                  return await servicePredictorApiService.calculateLost({
                    baseNodeData: {},
                    remoteNodeData: {
                      latitude: 37.417749,
                      longitude: -121.874486,
                    },
                    operatorKey,
                    operator: 'Sail Internet',
                    email: 'admin@taranawireless.com',
                    isMultiSector: true,
                  });
                }}
              />
            </Paper>
          </>
        )}
      </Container>
    </Box>
  );
};

export default About;
